import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { EstadoTag, fechaHora, parametrosEnlace, poli, qs } from './poliApi';
import DetallePrueba from './DetallePrueba';
import NuevaSolicitud from './NuevaSolicitud';

/**
 * P-1 Tablero de pruebas. El cliente administrativo ve solo sus companias (el servidor aplica el
 * filtro); Administrador, Gerencia y Poligrafista ven todas. Clic en una fila abre el detalle (P-3).
 */
const PoliSolicitudes = () => {
    const toast = useRef(null);
    const [cfg, setCfg] = useState(null);
    const [filtros, setFiltros] = useState({ companiaId: null, estado: null, texto: '', desde: '', hasta: '' });
    const [filas, setFilas] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [detalle, setDetalle] = useState(null);
    const [nueva, setNueva] = useState(false);

    const avisar = (severity, summary, detail) => toast.current && toast.current.show({ severity, summary, detail, life: 4500 });

    useEffect(() => {
        poli('/config').then(setCfg).catch((e) => avisar('error', 'Error', e.message));
    }, []);

    const buscar = useCallback(async (f = filtros) => {
        setCargando(true);
        try {
            setFilas(await poli(`/pruebas?${qs(f)}`));
        } catch (e) {
            avisar('error', 'Error', e.message);
        } finally {
            setCargando(false);
        }
    }, [filtros]);

    // Enlaces de los correos: ?prueba=ID abre esa prueba (el servidor valida la compania); ?solicitud=COD filtra
    useEffect(() => {
        if (!cfg) return;
        const q = parametrosEnlace();
        if (q.get('solicitud')) {
            const f = { ...filtros, texto: q.get('solicitud') };
            setFiltros(f);
            buscar(f);
        } else {
            buscar();
        }
        if (q.get('prueba') && /^\d+$/.test(q.get('prueba'))) setDetalle(Number(q.get('prueba')));
    }, [cfg]); // eslint-disable-line react-hooks/exhaustive-deps

    const resumen = useMemo(() => {
        const r = {};
        filas.forEach((f) => { r[f.estado] = (r[f.estado] || 0) + 1; });
        return r;
    }, [filas]);

    const puedeSolicitar = cfg && (cfg.perfiles.includes('CLIENTE') || cfg.perfiles.includes('ADMIN'));
    const companias = ((cfg && cfg.companias) || []).map((c) => ({ label: c.nombre, value: c.compania_id }));
    const estados = ((cfg && cfg.estados) || []).map((e) => ({ label: `${e.estado} · ${e.nombre}`, value: e.estado }));

    return (
        <div className="card">
            <Toast ref={toast} />
            <div className="flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                <h4 className="m-0">Pruebas de poligrafia</h4>
                {puedeSolicitar && <Button label="Nueva solicitud" icon="pi pi-plus" onClick={() => setNueva(true)} disabled={!companias.length}
                    tooltip={!companias.length ? 'Su usuario aun no tiene companias asignadas' : undefined} />}
            </div>
            {cfg && (
                <div className="flex flex-wrap gap-2 mb-3">
                    {cfg.estados.filter((e) => resumen[e.estado]).map((e) => (
                        <button key={e.estado} type="button" className="p-link border-round px-3 py-2"
                            style={{ border: `1px solid ${e.color}`, background: filtros.estado === e.estado ? e.color : 'transparent', color: filtros.estado === e.estado ? '#fff' : 'inherit' }}
                            onClick={() => { const f = { ...filtros, estado: filtros.estado === e.estado ? null : e.estado }; setFiltros(f); buscar(f); }}>
                            <b style={{ fontVariantNumeric: 'tabular-nums' }}>{resumen[e.estado]}</b> {e.nombre}
                        </button>
                    ))}
                </div>
            )}
            <div className="flex flex-wrap align-items-end gap-3 mb-3">
                {companias.length > 1 && (
                    <div className="flex flex-column gap-1" style={{ flex: '1 1 12rem' }}>
                        <label htmlFor="pol-f-compania">Compania</label>
                        <Dropdown inputId="pol-f-compania" value={filtros.companiaId} options={companias} showClear placeholder="Todas"
                            onChange={(e) => setFiltros({ ...filtros, companiaId: e.value })} />
                    </div>
                )}
                <div className="flex flex-column gap-1" style={{ flex: '1 1 12rem' }}>
                    <label htmlFor="pol-f-estado">Estado</label>
                    <Dropdown inputId="pol-f-estado" value={filtros.estado} options={estados} showClear placeholder="Todos"
                        onChange={(e) => setFiltros({ ...filtros, estado: e.value })} />
                </div>
                <div className="flex flex-column gap-1" style={{ flex: '2 1 16rem' }}>
                    <label htmlFor="pol-f-texto">Colaborador, cedula, codigo o grupo</label>
                    <InputText id="pol-f-texto" value={filtros.texto} onChange={(e) => setFiltros({ ...filtros, texto: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') buscar(); }} />
                </div>
                <div className="flex flex-column gap-1" style={{ flex: '0 1 10rem' }}>
                    <label htmlFor="pol-f-desde">Desde</label>
                    <InputText id="pol-f-desde" type="date" value={filtros.desde} onChange={(e) => setFiltros({ ...filtros, desde: e.target.value })} />
                </div>
                <div className="flex flex-column gap-1" style={{ flex: '0 1 10rem' }}>
                    <label htmlFor="pol-f-hasta">Hasta</label>
                    <InputText id="pol-f-hasta" type="date" value={filtros.hasta} onChange={(e) => setFiltros({ ...filtros, hasta: e.target.value })} />
                </div>
                <Button label="Buscar" icon="pi pi-search" onClick={() => buscar()} loading={cargando} />
            </div>
            <DataTable value={filas} size="small" paginator rows={20} loading={cargando} rowHover dataKey="prueba_id"
                onRowClick={(e) => setDetalle(e.data.prueba_id)} style={{ cursor: 'pointer' }} emptyMessage="Sin pruebas con esos filtros" responsiveLayout="scroll">
                <Column header="Colaborador" sortable sortField="apellidos" body={(r) => <div><b>{r.apellidos} {r.nombres}</b><br /><small className="text-600">{r.cedula}</small></div>} />
                <Column header="Compania / proyecto" body={(r) => <div>{r.compania}<br /><small className="text-600">{r.proyecto}</small></div>} />
                <Column header="Motivo / grupo" body={(r) => <div>{r.motivo}<br /><small className="text-600">{r.nombre_grupo}</small></div>} />
                <Column field="tipo_prueba" header="Tipo" />
                <Column header="Cita" sortable sortField="fecha_asignada" body={(r) => fechaHora(r.fecha_asignada)} />
                <Column header="Estado" sortable sortField="estado" body={(r) => <EstadoTag fila={r} />} />
                <Column field="codigo" header="Codigo" />
            </DataTable>

            <DetallePrueba pruebaId={detalle} cfg={cfg} alCerrar={() => setDetalle(null)} alCambiar={() => buscar()} />
            <NuevaSolicitud visible={nueva} cfg={cfg} alCerrar={() => setNueva(false)}
                alCrear={(r) => { setNueva(false); avisar('success', 'Solicitud enviada', `${r.solicitud}: ${r.pruebas.length} pruebas en estado «Solicitada»`); buscar(); }} />
        </div>
    );
};

export default PoliSolicitudes;
